import { useId, useMemo } from 'react'
import { CalendarClock, X } from 'lucide-react'
import DatePicker from './DatePicker'
import TimePicker from './TimePicker'
import { kit } from './strings'
import './DateTimePicker.css'

/** Splits `YYYY-MM-DDTHH:mm` into its two halves; either may be missing. */
function splitIso(isoString) {
  if (!isoString) return { date: '', time: '' }
  const text = String(isoString).trim()
  const m = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}))?/.exec(text)
  if (!m) return { date: text.slice(0, 10), time: '' }
  return { date: m[1], time: m[2] || '' }
}

/**
 * A moment — the date and the clock time — as ONE field.
 *
 * It used to be two: a date field, then a separate bordered box holding a native
 * `<input type="time">`. Two shells side by side read as two unrelated questions, and
 * the native input brought its own glyph, its own US `--:-- --` mask and a browser popup
 * that matched nothing around it.
 *
 * Both halves are `bare` now and the shell around them carries the border, the hover wash
 * and the focus ring — the arrangement `DateRangeField` already uses to make from/to read
 * as one range. A short rule separates the halves; a single leading glyph stands for the
 * whole value rather than one per half.
 *
 * Emits `YYYY-MM-DDTHH:mm`.
 */
export default function DateTimePicker({
  id,
  value = '',
  onChange,
  min,
  max,
  disabled = false,
  invalid = false,
  clearable = false,
  placeholder = 'DD-MM-YYYY',
  timePlaceholder = 'HH:mm',
  /* A date chosen on its own still needs an hour. Nine in the morning is when a
     message is meant to arrive; midnight is only ever an accident of the format. */
  defaultTime = '09:00',
  minuteStep = 5,
  className = '',
}) {
  const generatedId = useId()
  const dateId = id || generatedId
  const timeId = `${dateId}-time`

  const { date, time } = useMemo(() => splitIso(value), [value])
  const minDate = useMemo(() => (min ? splitIso(min).date : undefined), [min])
  const maxDate = useMemo(() => (max ? splitIso(max).date : undefined), [max])

  const setDate = (nextDate) => {
    if (!nextDate) return onChange?.('')
    return onChange?.(`${nextDate}T${time || defaultTime}`)
  }

  /* A time picked before a date means today at that time — the alternative is
     discarding what the person just chose. */
  const setTime = (nextTime) => {
    if (!nextTime) return onChange?.(date ? `${date}T00:00` : '')
    const onDate = date || new Date().toLocaleDateString('en-CA')
    return onChange?.(`${onDate}T${nextTime}`)
  }

  const showClear = clearable && Boolean(value) && !disabled

  return (
    <div
      className={`datetime-picker${invalid ? ' is-invalid' : ''}${
        disabled ? ' is-disabled' : ''
      } ${className}`.trim()}
    >
      <CalendarClock
        className="datetime-picker__icon"
        size={16}
        strokeWidth={1.75}
        aria-hidden="true"
      />

      <DatePicker
        id={dateId}
        variant="bare"
        value={date}
        onChange={setDate}
        min={minDate}
        max={maxDate}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={placeholder}
      />

      <span className="datetime-picker__rule" aria-hidden="true" />

      <TimePicker
        id={timeId}
        variant="bare"
        value={time}
        onChange={setTime}
        step={minuteStep}
        disabled={disabled}
        placeholder={timePlaceholder}
        aria-label={kit.time}
      />

      {showClear && (
        <button
          type="button"
          className="datetime-picker__clear"
          onClick={() => onChange?.('')}
          aria-label={kit.clearDate}
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      )}
    </div>
  )
}
